import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { getAttendance, setAttendance, RECENTS_MAX } from '../sdk/saleSelectionStorage.js';
import { biddingStore } from './bidding.store.js';
import { saleIndexStore } from './sale-index.store.js';

/**
 * Which sale am I in.
 *
 * It owns the selected sale and the recent-sales quick-switch, and remembers
 * both on the device through the storage port. That device-scoped half is what
 * made this look like a mobile store; it is not. "Which sale am I in" is a
 * shared concept — a desktop surface attending a sale is attending the same
 * sale, and would otherwise have written this again — and the device-scoped
 * part is a port rather than a platform.
 *
 * What did NOT come with it is the three members that read the SURFACE's
 * filtered tree: `scopedGroups`, `hasScopedLots` and `selectedSaleTitle` narrow
 * `visibleGroups` to the attended sale, and `visibleGroups` is one screen's
 * answer to having no room for the whole block. They recompute from
 * `selectedSale` on whichever surface is drawing.
 *
 * It imports biddingStore one-way — biddingStore never imports this — so the
 * global grouping and the notification path stay decoupled.
 */

export const attendanceStore = defineStore('attendanceStore', () => {
  const bidding = biddingStore();
  const saleIndex = saleIndexStore();

  // BY THE SALE'S MINTED ID, both of them. The name stood a dealer in a sale
  // that no longer existed the moment it was renamed, and pointed at the wrong
  // week whenever a weekly name came round again.
  const selectedSale = ref(null);
  const recentSales = ref([]);
  const hydrated = ref(false);
  // The names a v1 device stored, held until the sale index can say which ids
  // they are. Never dropped and never guessed at, and never cleared by
  // validation — validation is about ids, and these are not ids yet.
  const pending = ref({ selectedSale: null, recentSales: [] });

  function pushRecent(sale) {
    recentSales.value = [sale, ...recentSales.value.filter((s) => s !== sale)].slice(0, RECENTS_MAX);
  }

  // The single entry point for "attend this sale". Auto-select routes through
  // it too, so the active sale is always in recents and therefore the switcher.
  function selectSale(sale) {
    if (!sale) return;
    selectedSale.value = sale;
    pushRecent(sale);
  }

  // Reconcile the selection against the loaded sales: prune what has ended. No-op until
  // hydrated so a persisted value isn't stomped before storage is read.
  function validateSelection() {
    if (!hydrated.value) return;
    const running = bidding.saleRefIds;
    if (!running.length) return; // lots not loaded yet — keep selection as-is
    // Prune any sale that has ended from recents.
    if (recentSales.value.some((id) => !running.includes(id))) {
      recentSales.value = recentSales.value.filter((id) => running.includes(id));
    }
    // Current selection still valid → keep it.
    if (selectedSale.value && running.includes(selectedSale.value)) return;
    // Otherwise nothing is attended. A sale is never picked for the dealer: a stored one that
    // has ended, or none ever chosen, leaves the Live tab to send them to choose one.
    selectedSale.value = null;
  }

  // Turn the v1 names into ids as far as the sale index allows, keeping the rest
  // pending for a later load. Order is kept: the recents row reads most recent
  // first, and a migration must not shuffle it.
  function resolvePending() {
    const held = pending.value;
    if (!held.selectedSale && held.recentSales.length === 0) return;
    const resolved = [];
    const stillPending = [];
    for (const name of held.recentSales) {
      const saleRefId = saleIndex.saleRefIdForName(name);
      if (saleRefId) resolved.push(saleRefId);
      else stillPending.push(name);
    }
    if (resolved.length) {
      recentSales.value = [...new Set([...recentSales.value, ...resolved])].slice(0, RECENTS_MAX);
    }
    let selectedName = held.selectedSale;
    if (selectedName) {
      const saleRefId = saleIndex.saleRefIdForName(selectedName);
      if (saleRefId) {
        selectedName = null;
        // Only if the dealer has not since attended something themselves.
        if (!selectedSale.value) selectSale(saleRefId);
      }
    }
    pending.value = { selectedSale: selectedName, recentSales: stillPending };
  }

  // Read persisted selection once, then migrate what is pending and validate.
  // Called when the Sales tab mounts, and before the Dashboard attends a sale.
  async function hydrate() {
    if (hydrated.value) return;
    const stored = await getAttendance();
    selectedSale.value = stored.selectedSale;
    recentSales.value = stored.recentSales;
    pending.value = stored.pending ?? { selectedSale: null, recentSales: [] };
    hydrated.value = true;
    resolvePending();
    validateSelection();
  }

  // Re-validate whenever the loaded sales change (first load, demo reset, a sale
  // ending). hydrate() drives the first pass, so no immediate here.
  watch(() => bidding.saleRefIds, validateSelection);

  // The index answers after the device does, and answers again on a later load,
  // so every pending name gets another chance each time it does.
  watch(() => saleIndex.sales.length, () => {
    if (!hydrated.value) return;
    resolvePending();
    validateSelection();
  });

  // Persist selection + recents. Best-effort; never blocks a switch.
  watch([selectedSale, recentSales, pending], () => {
    if (!hydrated.value) return;
    void setAttendance({
      selectedSale: selectedSale.value,
      recentSales: recentSales.value,
      pending: pending.value
    });
  });

  return {
    selectedSale,
    recentSales,
    hydrated,
    // The v1 names still waiting on the index. Held, not lost.
    pending,
    selectSale,
    hydrate,
    validateSelection
  };
});
